import { cn } from '@heidi/utils';
import * as React from 'react';

import { useDesignSystemIntl } from '../hooks/use-design-system-intl/use-design-system-intl';
import { TypographyP2 } from '../typography';
import type { HeidiLoaderProps } from './heidi-loader.types';
import { LogoAnimation } from './logo-animation';

const HeidiLoader = React.forwardRef<HTMLDivElement, HeidiLoaderProps>((props, ref) => {
  const { size, className, 'data-testid': testId = 'heidi-loader' } = props;
  const decorative = props.decorative ?? false;
  // Called unconditionally (Rules of Hooks); a decorative mark has nothing to format.
  const intl = useDesignSystemIntl();

  // Only loader mode carries the reduced-motion pulse (a progress cue); a
  // decorative mark has nothing to signal and stills.
  const markClassName = cn(
    'shrink-0',
    !decorative && 'motion-reduce:animate-pulse',
    size == null && 'size-[105px]',
    className
  );
  const markStyle = size == null ? undefined : { width: size, height: size };

  if (props.decorative) {
    return (
      <div ref={ref} className={markClassName} style={markStyle} data-testid={testId}>
        <LogoAnimation className="size-full" />
      </div>
    );
  }

  const { loadingText, typographyClassName, CustomTypographyComponent } = props;
  const hasVisibleText = Boolean(loadingText) || Boolean(CustomTypographyComponent);
  // role="status" is a live region; with no visible text the aria-label carries
  // the announcement, otherwise the visible text does.
  const ariaLabel = hasVisibleText
    ? undefined
    : intl.formatMessage({ defaultMessage: 'Loading', id: 'iFsDVRnIHW' });

  return (
    <div
      ref={ref}
      className="flex size-full flex-col items-center justify-center"
      data-testid={testId}
      role="status"
      aria-label={ariaLabel}
    >
      <div className="flex flex-col items-center justify-normal gap-2">
        <div className={markClassName} style={markStyle} data-testid={`${testId}-mark`}>
          <LogoAnimation className="size-full" />
        </div>
        {hasVisibleText && (
          <TypographyComponent
            loadingText={loadingText}
            typographyClassName={typographyClassName}
            CustomTypographyComponent={CustomTypographyComponent}
          />
        )}
      </div>
    </div>
  );
});
HeidiLoader.displayName = 'HeidiLoader';

const TypographyComponent = ({
  loadingText,
  typographyClassName,
  CustomTypographyComponent,
}: {
  loadingText?: string;
  typographyClassName?: string;
  CustomTypographyComponent?: React.ReactNode;
}) => {
  if (CustomTypographyComponent) return <>{CustomTypographyComponent}</>;
  if (!loadingText) return null;
  return (
    <TypographyP2 className={cn('text-text-primary', typographyClassName)}>
      {loadingText}
    </TypographyP2>
  );
};

export { HeidiLoader };
